import { useEffect, useState } from 'react';
import { useForm } from '@tanstack/react-form';
import { z } from 'zod';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FieldError } from '@/components/form/field-error';
import { useAdminApiMutation } from '@/hooks/use-admin-api-query';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface Subscription {
  id: string;
  planSlug: string;
  status: string;
  stripeSubscriptionId: string | null;
  currentPeriodEnd: string | null;
  customMessageQuota?: number;
  customPriceCents?: number;
  customInterval?: string;
  customTopUpRateMicros?: number;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function statusBadgeVariant(
  status: string,
): 'success' | 'destructive' | 'secondary' {
  if (status === 'active') return 'success';
  if (status === 'canceled' || status === 'past_due') return 'destructive';
  return 'secondary';
}

const decimalStringSchema = (message: string) =>
  z
    .string()
    .trim()
    .refine((value) => /^\d+(\.\d+)?$/.test(value) && Number(value) >= 0, message);

const customPlanSchema = z.object({
  quota: z
    .string()
    .trim()
    .refine((value) => /^\d+$/.test(value), 'Quota must be a non-negative whole number'),
  priceDollars: decimalStringSchema('Price must be a non-negative decimal'),
  rateDollars: decimalStringSchema('Top-up rate must be a non-negative decimal'),
  billingInterval: z.enum(['month', 'year']),
  stripePriceId: z.string(),
});

type CustomPlanForm = z.infer<typeof customPlanSchema>;

function formDefaults(subscription: Subscription | null): CustomPlanForm {
  return {
    quota: String(subscription?.customMessageQuota ?? 1000),
    priceDollars: String((subscription?.customPriceCents ?? 0) / 100),
    rateDollars: String((subscription?.customTopUpRateMicros ?? 1000) / 1_000_000),
    billingInterval: subscription?.customInterval === 'year' ? 'year' : 'month',
    stripePriceId: '',
  };
}

export function WorkspaceBillingCard({
  subscription,
  workspacePublicId,
  onChanged,
}: {
  subscription: Subscription | null;
  workspacePublicId: string;
  onChanged: () => void;
}) {
  const [formOpen, setFormOpen] = useState(false);
  const saveCustomPlan = useAdminApiMutation<unknown, CustomPlanForm>((value) => ({
    path: `/super-admin/workspaces/${workspacePublicId}/custom-plan`,
    init: {
      method: 'PUT',
      body: JSON.stringify({
        messageQuota: Number(value.quota),
        priceCents: Math.round(Number(value.priceDollars) * 100),
        topUpRateMicros: Math.round(Number(value.rateDollars) * 1_000_000),
        interval: value.billingInterval,
        stripePriceId: value.stripePriceId || undefined,
      }),
    },
  }));
  const clearCustomPlan = useAdminApiMutation<unknown, void>(() => ({
    path: `/super-admin/workspaces/${workspacePublicId}/custom-plan`,
    init: {
      method: 'PUT',
      body: JSON.stringify({ clear: true, restoreToSlug: 'free' }),
    },
  }));

  const form = useForm({
    defaultValues: formDefaults(subscription),
    validators: { onSubmit: customPlanSchema },
    onSubmit: async ({ value }) => {
      await saveCustomPlan.mutateAsync(value);
      setFormOpen(false);
      onChanged();
    },
  });

  useEffect(() => {
    form.reset(formDefaults(subscription));
  }, [form, subscription]);

  async function handleClear() {
    await clearCustomPlan.mutateAsync();
    onChanged();
  }

  return (
    <Card>
      <CardHeader>
        <p className="text-base font-semibold">Billing</p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {!subscription ? (
            <p className="text-sm text-muted-foreground">No subscription</p>
          ) : (
            <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-3">
              <span className="text-xs font-semibold text-muted-foreground">
                Plan
              </span>
              <span className="text-sm">
                {capitalize(subscription.planSlug)}
              </span>

              <span className="text-xs font-semibold text-muted-foreground">
                Status
              </span>
              <span>
                <Badge variant={statusBadgeVariant(subscription.status)}>
                  {capitalize(subscription.status)}
                </Badge>
              </span>

              <span className="text-xs font-semibold text-muted-foreground">
                Stripe Subscription
              </span>
              <span className="font-mono text-xs">
                {subscription.stripeSubscriptionId || 'None'}
              </span>

              <span className="text-xs font-semibold text-muted-foreground">
                Period End
              </span>
              <span className="text-sm">
                {formatDate(subscription.currentPeriodEnd)}
              </span>

              {subscription.planSlug === 'custom' && (
                <>
                  <span className="text-xs font-semibold text-muted-foreground">
                    Custom Quota
                  </span>
                  <span className="text-sm">
                    {subscription.customMessageQuota != null
                      ? subscription.customMessageQuota.toLocaleString()
                      : 'N/A'}
                  </span>

                  <span className="text-xs font-semibold text-muted-foreground">
                    Custom Price
                  </span>
                  <span className="text-sm">
                    {subscription.customPriceCents != null
                      ? `$${(subscription.customPriceCents / 100).toFixed(2)}`
                      : 'N/A'}
                  </span>

                  <span className="text-xs font-semibold text-muted-foreground">
                    Interval
                  </span>
                  <span className="text-sm capitalize">
                    {subscription.customInterval ?? 'N/A'}
                  </span>

                  <span className="text-xs font-semibold text-muted-foreground">
                    Top-Up Rate
                  </span>
                  <span className="text-sm">
                    {subscription.customTopUpRateMicros != null
                      ? `$${(subscription.customTopUpRateMicros / 1_000_000).toFixed(6)}/msg`
                      : 'N/A'}
                  </span>
                </>
              )}
            </div>
          )}

          <div className="border-t pt-4">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setFormOpen((o) => !o)}
              className="h-auto gap-1.5 px-0 text-muted-foreground hover:bg-transparent hover:text-foreground"
            >
              {formOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              Set Custom Plan
            </Button>

            {formOpen && (
              <form
                noValidate
                className="mt-3 space-y-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  void form.handleSubmit();
                }}
              >
                <div className="grid grid-cols-2 gap-3">
                  <form.Field name="quota">
                    {(field) => {
                      const hasError = field.state.meta.errors.length > 0;
                      return (
                        <div className="space-y-1">
                          <Label htmlFor="custom-plan-quota" className="text-xs font-semibold text-muted-foreground">
                            Quota (per month)
                          </Label>
                          <Input
                            id="custom-plan-quota"
                            type="number"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            aria-invalid={hasError || undefined}
                            aria-describedby={hasError ? 'custom-plan-quota-error' : undefined}
                            placeholder="1000"
                          />
                          <FieldError id="custom-plan-quota-error" errors={field.state.meta.errors} />
                        </div>
                      );
                    }}
                  </form.Field>
                  <form.Field name="priceDollars">
                    {(field) => {
                      const hasError = field.state.meta.errors.length > 0;
                      return (
                        <div className="space-y-1">
                          <Label htmlFor="custom-plan-price" className="text-xs font-semibold text-muted-foreground">
                            Price ($)
                          </Label>
                          <Input
                            id="custom-plan-price"
                            type="number"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            aria-invalid={hasError || undefined}
                            aria-describedby={hasError ? 'custom-plan-price-error' : undefined}
                            placeholder="49.00"
                            step="0.01"
                          />
                          <FieldError id="custom-plan-price-error" errors={field.state.meta.errors} />
                        </div>
                      );
                    }}
                  </form.Field>
                  <form.Field name="rateDollars">
                    {(field) => {
                      const hasError = field.state.meta.errors.length > 0;
                      return (
                        <div className="space-y-1">
                          <Label htmlFor="custom-plan-rate" className="text-xs font-semibold text-muted-foreground">
                            Top-Up Rate ($/msg)
                          </Label>
                          <Input
                            id="custom-plan-rate"
                            type="number"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            aria-invalid={hasError || undefined}
                            aria-describedby={hasError ? 'custom-plan-rate-error' : undefined}
                            placeholder="0.001"
                            step="0.000001"
                          />
                          <FieldError id="custom-plan-rate-error" errors={field.state.meta.errors} />
                        </div>
                      );
                    }}
                  </form.Field>
                  <form.Field name="billingInterval">
                    {(field) => (
                      <div className="space-y-1">
                        <Label htmlFor="custom-plan-interval" className="text-xs font-semibold text-muted-foreground">
                          Interval
                        </Label>
                        <Select
                          value={field.state.value}
                          onValueChange={(v) =>
                            field.handleChange(v === 'year' ? 'year' : 'month')
                          }
                        >
                          <SelectTrigger id="custom-plan-interval" className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="month">Monthly</SelectItem>
                            <SelectItem value="year">Yearly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </form.Field>
                  <form.Field name="stripePriceId">
                    {(field) => (
                      <div className="col-span-2 space-y-1">
                        <Label htmlFor="custom-plan-stripe-price" className="text-xs font-semibold text-muted-foreground">
                          Stripe Price ID (optional)
                        </Label>
                        <Input
                          id="custom-plan-stripe-price"
                          type="text"
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                          placeholder="price_..."
                        />
                      </div>
                    )}
                  </form.Field>
                </div>

                <div className="flex gap-2">
                  <form.Subscribe
                    selector={(state) => ({
                      canSubmit: state.canSubmit,
                      isSubmitting: state.isSubmitting,
                    })}
                  >
                    {({ canSubmit, isSubmitting }) => (
                      <Button
                        type="submit"
                        size="sm"
                        disabled={
                          !canSubmit ||
                          isSubmitting ||
                          saveCustomPlan.isPending ||
                          clearCustomPlan.isPending
                        }
                      >
                        {isSubmitting || saveCustomPlan.isPending
                          ? 'Saving...'
                          : 'Save Custom Plan'}
                      </Button>
                    )}
                  </form.Subscribe>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    onClick={() => void handleClear()}
                    disabled={clearCustomPlan.isPending || saveCustomPlan.isPending}
                  >
                    {clearCustomPlan.isPending ? 'Clearing...' : 'Clear Custom Plan'}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
